import classNames from 'classnames'
import { motion } from 'framer-motion'
import { t } from 'i18next'
import React from 'react'

import { useModalInstance, useModalTabsOptional } from '@context/ModalInstanceContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { SearchFieldProps } from '@ui/SearchField/SearchField'
import { Heading6 } from '@ui/Typography/Typography'
import { IconButton, IconButtonProps } from '@ui/components/Button'
import { Input } from '@ui/components/Input'
import { SegmentedControl } from '@ui/components/SegmentedControl/SegmentedControl'
import { Tabs } from '@ui/components/Tabs'
import { CircleCloseIcon } from '@ui/icons/CircleClose.icon'
import { CloseIcon } from '@ui/icons/Close.icon'
import { SearchIcon } from '@ui/icons/Search.icon'
import { IconFCType } from '@ui/icons/types'

import { Box } from '@shared/components/Box'

import { useBottomSheetDrag } from '../Container/BottomSheet/DragContext'

import styles from './ModalHeader.module.scss'

type ModalHeaderIconProps =
  | { icon?: IconFCType; iconButton?: never }
  | { icon?: never; iconButton?: IconButtonProps }

export type ModalHeaderProps = ModalHeaderIconProps & {
  canClose?: boolean
  children?: React.ReactNode
  search?: SearchFieldProps
  sensitive?: boolean
}

export const MODAL_HEADER_GAP = 16

export const ModalHeader: React.FC<ModalHeaderProps> = (props) => {
  const { icon: Icon, iconButton, canClose, sensitive, children, search } = props
  const { close, containerMode } = useModalInstance()
  const dragHandlers = useBottomSheetDrag()
  const tabsCtx = useModalTabsOptional()

  const isBottomSheet = !!dragHandlers
  const showCloseButton =
    canClose !== undefined ? canClose : containerMode ? containerMode === 'fullScreen' : false

  const ModalTabs = () => {
    if (tabsCtx && tabsCtx.items.length > 0) {
      if (tabsCtx.variant === 'segmented-control') {
        return (
          <div className={classNames(styles.headerTabs, styles.fullWidth)}>
            <SegmentedControl
              size={tabsCtx.size === 'xs' ? 'sm' : 'md'}
              variant='default'
              activeTab={tabsCtx.activeId}
              tabs={tabsCtx.items.map((i) => ({
                id: i.id,
                name: i.label,
                iconLeft: i.iconLeft,
                iconRight: i.iconRight,
                onClick: () => tabsCtx.setActiveId(i.id),
              }))}
            />
          </div>
        )
      }

      if (tabsCtx.variant === 'tabs') {
        return (
          <div className={styles.headerTabs}>
            <Tabs
              size={tabsCtx.size}
              initialIndex={Math.max(
                0,
                tabsCtx.items.findIndex((i) => i.id === tabsCtx.activeId)
              )}
              tabs={tabsCtx.items.map((i) => ({
                id: i.id,
                label: i.label,
                iconLeft: i.iconLeft,
                iconRight: i.iconRight,
                alert: i.alert,
              }))}
              onTabClick={(t) => tabsCtx.setActiveId(String(t.id))}
            />
          </div>
        )
      }
    }
  }

  return (
    <motion.div
      className={classNames(styles.header, {
        [styles.canDrag]: isBottomSheet,
        [styles.fullScreen]: containerMode === 'fullScreen',
      })}
      style={{ gap: MODAL_HEADER_GAP }}
      {...dragHandlers}
    >
      <Box flex fullWidth align='center' justify='center' alignSelf='stretch' gap='x16'>
        {Icon && <Icon />}
        {iconButton && <IconButton {...iconButton} />}
        <Heading6 medium className={styles.title}>
          {sensitive ? <SensitiveData>{children}</SensitiveData> : children}
        </Heading6>
        {showCloseButton && (
          <IconButton.Secondary
            size='sm'
            aria-label='Close modal'
            onClick={close}
            icon={{ icon: CloseIcon, color: 'text-med-em' }}
          />
        )}
      </Box>

      {search && (
        <Input
          label={search.label ?? t('Search')}
          value={search.value ?? ''}
          disabled={search.disabled}
          leftIcon={SearchIcon}
          fullWidth
          autoFocus
          rightIcon={
            search.canClear && (search.value?.length ?? 0) > 0 ? CircleCloseIcon : undefined
          }
          width='100%'
          onEndIconClick={() => (search.canClear ? search.onChange('') : undefined)}
          onChange={(e) => search.onChange(e.target.value)}
        />
      )}
      <ModalTabs />
    </motion.div>
  )
}
